import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  ArrowLeft, BookOpen, Cross, Sun, Moon, Heart, 
  ShareNetwork, Check, CaretLeft, CaretRight,
  Calendar, Sparkle, HandsPraying, Copy,
  Play, Pause, SpeakerHigh, SpeakerSlash, Spinner
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import ShareModal from '../components/ShareModal';

const API = process.env.REACT_APP_BACKEND_URL;

// Theme-based background images
const THEME_BACKGROUNDS = {
  purpose: 'https://images.unsplash.com/photo-1551142394-d488b4b88eb6?w=1600&auto=format&fit=crop&q=80',
  discipline: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=1600&auto=format&fit=crop&q=80',
  brotherhood: 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=1600&auto=format&fit=crop&q=80',
  wealth: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=1600&auto=format&fit=crop&q=80',
  action: 'https://images.unsplash.com/photo-1543525238-54e3d131f7ca?w=1600&auto=format&fit=crop&q=80',
  leadership: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=1600&auto=format&fit=crop&q=80',
  wisdom: 'https://images.pexels.com/photos/9783072/pexels-photo-9783072.jpeg?auto=compress&cs=tinysrgb&w=1600',
  stewardship: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1600&auto=format&fit=crop&q=80',
  integrity: 'https://images.unsplash.com/photo-1507692049790-de58290a4334?w=1600&auto=format&fit=crop&q=80',
  perseverance: 'https://images.unsplash.com/photo-1552674605-db6ffd4facb5?w=1600&auto=format&fit=crop&q=80',
  faith: 'https://images.unsplash.com/photo-1504052434569-70ad5836ab65?w=1600&auto=format&fit=crop&q=80',
  provision: 'https://images.pexels.com/photos/19248218/pexels-photo-19248218.jpeg?auto=compress&cs=tinysrgb&w=1600',
  default: 'https://images.unsplash.com/photo-1771134992909-6eaf1289beab?w=1600&auto=format&fit=crop&q=80'
};

// Theme accent colors
const THEME_COLORS = {
  purpose: '#C4A661',
  discipline: '#EF4444',
  brotherhood: '#8B5CF6',
  wealth: '#10B981',
  action: '#F59E0B',
  leadership: '#3B82F6',
  wisdom: '#C4A661',
  stewardship: '#06B6D4',
  integrity: '#EC4899',
  perseverance: '#F97316',
  faith: '#8B5CF6',
  provision: '#10B981',
  default: '#C4A661'
};

const DailyDevotionalPage = () => {
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [devotional, setDevotional] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isCompleted, setIsCompleted] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [allDevotionals, setAllDevotionals] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  
  // Audio player state
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [audioError, setAudioError] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  
  const today = new Date();
  const dayOfMonth = today.getDate();
  const formattedDate = today.toLocaleDateString('en-US', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  // Share devotional
  const openShareModal = () => {
    setShareModalOpen(true);
  };

  const getShareData = () => {
    if (!devotional) return { url: '', text: '', title: '' };
    return {
      url: `${window.location.origin}/devotionals?day=${devotional.day}`,
      text: `"${devotional.scripture}" — ${devotional.reference}\n\nToday's devotional: ${devotional.title}`,
      title: `Share Today's Devotional: ${devotional.title}`
    };
  };

  useEffect(() => {
    fetchTodayDevotional();
  }, [selectedDay]);

  const fetchTodayDevotional = async () => {
    try {
      setLoading(true);
      const day = selectedDay || dayOfMonth;
      const url = selectedDay 
        ? `${API}/api/devotional/day/${day}`
        : `${API}/api/devotional/today`;
      
      const headers = user ? getAuthHeader() : {};
      const res = await axios.get(url, { headers });
      setDevotional(res.data);
      setIsCompleted(res.data.is_completed || false);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchAllDevotionals = async () => {
    try {
      const headers = user ? getAuthHeader() : {};
      const res = await axios.get(`${API}/api/devotionals/archive`, { headers });
      setAllDevotionals(res.data);
    } catch (error) {
    }
  };

  const handleMarkComplete = async () => {
    if (!user) {
      toast.error('Please sign in to track progress');
      return;
    }
    
    try {
      await axios.post(
        `${API}/api/devotional/complete`,
        { day: devotional.day },
        { headers: getAuthHeader() }
      );
      setIsCompleted(true);
      toast.success('Devotional marked as complete!');
    } catch (error) {
      toast.error('Failed to mark complete');
    }
  };

  const handleShare = () => {
    // Open the share modal instead of native share
    setShareModalOpen(true);
  };

  const handlePrevDay = () => {
    const currentDay = selectedDay || dayOfMonth;
    if (currentDay > 1) {
      setSelectedDay(currentDay - 1);
    }
  };

  const handleNextDay = () => {
    const currentDay = selectedDay || dayOfMonth;
    if (currentDay < 31) {
      setSelectedDay(currentDay + 1);
    }
  };

  const handleSelectDay = (day) => {
    setSelectedDay(day);
    setShowArchive(false);
  };

  // Audio player functions
  const getAudioUrl = () => {
    const day = selectedDay || dayOfMonth;
    return `${API}/api/devotional/audio/${day}`;
  };

  const handlePlayPause = async () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      setAudioLoading(true);
      setAudioError(false);
      try {
        await audioRef.current.play();
        setIsPlaying(true);
      } catch (error) {
        setAudioError(true);
      } finally {
        setAudioLoading(false);
      }
    }
  };

  const handleAudioTimeUpdate = () => {
    if (audioRef.current) {
      setAudioProgress(audioRef.current.currentTime);
    }
  };

  const handleAudioLoadedMetadata = () => {
    if (audioRef.current) {
      setAudioDuration(audioRef.current.duration);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
    setAudioProgress(0);
  };

  const handleAudioError = () => {
    setAudioError(true);
    setIsPlaying(false);
    setAudioLoading(false);
  };

  const handleSeek = (e) => {
    if (audioRef.current && audioDuration) {
      const rect = e.currentTarget.getBoundingClientRect();
      const percent = (e.clientX - rect.left) / rect.width;
      audioRef.current.currentTime = percent * audioDuration;
    }
  };

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getBackground = () => {
    if (!devotional) return THEME_BACKGROUNDS.default;
    return THEME_BACKGROUNDS[devotional.theme] || THEME_BACKGROUNDS.default;
  };

  const getAccentColor = () => {
    if (!devotional) return THEME_COLORS.default;
    return THEME_COLORS[devotional.theme] || THEME_COLORS.default;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-[#C4A661] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#A3A3A3]">Loading today's word...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Full-Screen Hero Background */}
      <div className="fixed inset-0 z-0">
        <motion.img
          key={devotional?.day}
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.5 }}
          src={getBackground()}
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/90" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
      </div>

      {/* Header */}
      <header className="relative z-20 sticky top-0">
        <div className="bg-gradient-to-b from-black/80 to-transparent">
          <div className="max-w-4xl mx-auto px-4 md:px-8 py-4">
            <div className="flex items-center justify-between">
              <button 
                onClick={() => navigate('/community')}
                className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
              >
                <ArrowLeft size={20} />
                <span className="text-sm hidden sm:inline">Back</span>
              </button>
              
              <div className="flex items-center gap-2">
                <Sun size={18} className="text-[#C4A661]" />
                <span className="text-white font-medium">Daily Devotional</span>
              </div>
              
              <button 
                onClick={() => {
                  fetchAllDevotionals();
                  setShowArchive(true);
                }}
                className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
              >
                <Calendar size={20} />
                <span className="text-sm hidden sm:inline">Archive</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 min-h-[calc(100vh-80px)] flex flex-col">
        {/* Day Navigation */}
        <div className="max-w-4xl mx-auto w-full px-4 md:px-8 pt-8">
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={handlePrevDay}
              disabled={(selectedDay || dayOfMonth) <= 1}
              className="w-10 h-10 rounded-full bg-white/10 backdrop-blur flex items-center justify-center text-white hover:bg-white/20 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <CaretLeft size={20} />
            </button>
            
            <div className="text-center">
              <p className="text-xs text-white/50 uppercase tracking-wider mb-1">
                {selectedDay ? 'Day' : 'Today'} {selectedDay || dayOfMonth} of 31
              </p>
              <p className="text-white/80 text-sm">
                {selectedDay 
                  ? `Day ${selectedDay}'s Devotional`
                  : formattedDate
                }
              </p>
            </div>
            
            <button
              onClick={handleNextDay}
              disabled={(selectedDay || dayOfMonth) >= 31}
              className="w-10 h-10 rounded-full bg-white/10 backdrop-blur flex items-center justify-center text-white hover:bg-white/20 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <CaretRight size={20} />
            </button>
          </div>
          
          {selectedDay && selectedDay !== dayOfMonth && (
            <button
              onClick={() => setSelectedDay(null)}
              className="mx-auto mt-3 block text-sm text-[#C4A661] hover:text-[#D4B671] transition-colors"
            >
              Return to today
            </button>
          )}
        </div>

        {/* Devotional Content */}
        <div className="flex-1 flex items-center justify-center px-4 md:px-8 py-8">
          <motion.div
            key={devotional?.day}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl w-full"
          >
            {/* Theme Badge */}
            <div className="flex items-center justify-center gap-2 mb-6">
              <span 
                className="px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider"
                style={{ 
                  backgroundColor: `${getAccentColor()}20`,
                  color: getAccentColor()
                }}
              >
                {devotional?.theme}
              </span>
              {isCompleted && (
                <span className="px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-green-500/20 text-green-400 flex items-center gap-1">
                  <Check size={12} weight="bold" />
                  Complete
                </span>
              )}
            </div>

            {/* Title */}
            <h1 className="text-3xl md:text-5xl font-black text-white text-center mb-6 leading-tight">
              {devotional?.title}
            </h1>

            {/* Audio Player */}
            <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl p-4 mb-8">
              <audio
                ref={audioRef}
                src={getAudioUrl()}
                onTimeUpdate={handleAudioTimeUpdate}
                onLoadedMetadata={handleAudioLoadedMetadata}
                onEnded={handleAudioEnded}
                onError={handleAudioError}
                preload="none"
              />
              
              <div className="flex items-center gap-4">
                {/* Play/Pause Button */}
                <button
                  onClick={handlePlayPause}
                  disabled={audioLoading}
                  className="w-12 h-12 rounded-full flex items-center justify-center transition-all"
                  style={{ 
                    backgroundColor: audioError ? '#EF4444' : getAccentColor(),
                    opacity: audioLoading ? 0.7 : 1
                  }}
                >
                  {audioLoading ? (
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  ) : audioError ? (
                    <SpeakerSlash size={20} className="text-white" weight="fill" />
                  ) : isPlaying ? (
                    <Pause size={20} className="text-black" weight="fill" />
                  ) : (
                    <Play size={20} className="text-black ml-1" weight="fill" />
                  )}
                </button>
                
                {/* Progress Bar */}
                <div className="flex-1">
                  <div 
                    className="h-2 bg-white/20 rounded-full cursor-pointer overflow-hidden"
                    onClick={handleSeek}
                  >
                    <div 
                      className="h-full rounded-full transition-all duration-150"
                      style={{ 
                        backgroundColor: getAccentColor(),
                        width: audioDuration ? `${(audioProgress / audioDuration) * 100}%` : '0%'
                      }}
                    />
                  </div>
                  <div className="flex justify-between mt-1 text-xs text-white/50">
                    <span>{formatTime(audioProgress)}</span>
                    <span>{formatTime(audioDuration)}</span>
                  </div>
                </div>
                
                {/* Mute Button */}
                <button
                  onClick={toggleMute}
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/20 transition-all"
                >
                  {isMuted ? (
                    <SpeakerSlash size={18} />
                  ) : (
                    <SpeakerHigh size={18} />
                  )}
                </button>
              </div>
              
              {audioError && (
                <p className="text-xs text-red-400 mt-2 text-center">
                  Audio not available. Generating narration...
                </p>
              )}
              
              {!audioError && !isPlaying && audioProgress === 0 && (
                <p className="text-xs text-white/50 mt-2 text-center">
                  Listen to today's devotional
                </p>
              )}
            </div>

            {/* Scripture Card */}
            <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-6 md:p-8 mb-8">
              <div className="flex items-center gap-2 mb-4">
                <BookOpen size={18} className="text-[#C4A661]" />
                <span className="text-xs font-bold uppercase tracking-wider text-[#C4A661]">
                  Today's Scripture
                </span>
              </div>
              
              <p className="text-xl md:text-2xl font-serif italic text-white leading-relaxed mb-4">
                "{devotional?.scripture}"
              </p>
              
              <p className="text-right font-medium" style={{ color: getAccentColor() }}>
                — {devotional?.reference}
              </p>
            </div>

            {/* Reflection */}
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Sparkle size={18} className="text-[#C4A661]" />
                <span className="text-xs font-bold uppercase tracking-wider text-[#C4A661]">
                  Reflection
                </span>
              </div>
              
              <p className="text-white/80 text-lg leading-relaxed">
                {devotional?.reflection}
              </p>
            </div>

            {/* Today's Action */}
            <div className="bg-gradient-to-br from-[#C4A661]/20 to-[#C4A661]/5 border border-[#C4A661]/30 rounded-2xl p-6 md:p-8 mb-8">
              <div className="flex items-center gap-2 mb-4">
                <HandsPraying size={18} className="text-[#C4A661]" weight="fill" />
                <span className="text-xs font-bold uppercase tracking-wider text-[#C4A661]">
                  Today's Action
                </span>
              </div>
              
              <p className="text-white font-medium text-lg">
                {devotional?.action}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              {!isCompleted && user && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleMarkComplete}
                  className="w-full sm:w-auto px-8 py-4 bg-[#C4A661] hover:bg-[#D4B671] text-[#0A0A0A] font-bold rounded-xl flex items-center justify-center gap-2 transition-colors"
                >
                  <Check size={20} weight="bold" />
                  Mark as Complete
                </motion.button>
              )}
              
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleShare}
                className="w-full sm:w-auto px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl flex items-center justify-center gap-2 transition-colors backdrop-blur"
              >
                <ShareNetwork size={20} />
                Share Devotional
              </motion.button>
            </div>
          </motion.div>
        </div>

        {/* Bottom Encouragement */}
        <div className="text-center pb-8 px-4">
          <p className="text-white/40 text-sm italic">
            "Walk in power. Lead with purpose."
          </p>
        </div>
      </main>

      {/* Archive Modal */}
      <AnimatePresence>
        {showArchive && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 backdrop-blur-md z-50 flex items-center justify-center p-4"
            onClick={() => setShowArchive(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[#141414] border border-[#262626] rounded-2xl p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-white">Devotional Archive</h3>
                <button 
                  onClick={() => setShowArchive(false)}
                  className="text-[#A3A3A3] hover:text-white"
                >
                  ✕
                </button>
              </div>
              
              <p className="text-[#A3A3A3] text-sm mb-6">
                Select a day to read that devotional. Each month cycles through 31 devotionals.
              </p>
              
              <div className="grid grid-cols-7 gap-2">
                {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                  <button
                    key={day}
                    onClick={() => handleSelectDay(day)}
                    className={`aspect-square rounded-lg flex items-center justify-center text-sm font-medium transition-all ${
                      day === dayOfMonth
                        ? 'bg-[#C4A661] text-[#0A0A0A]'
                        : day === selectedDay
                        ? 'bg-[#C4A661]/30 text-[#C4A661] border border-[#C4A661]'
                        : 'bg-[#262626] text-[#A3A3A3] hover:bg-[#363636] hover:text-white'
                    }`}
                  >
                    {day}
                  </button>
                ))}
              </div>
              
              <p className="text-center text-xs text-[#525252] mt-4">
                Today is Day {dayOfMonth}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Share Modal */}
      <ShareModal
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        title={getShareData().title}
        shareUrl={getShareData().url}
        shareText={getShareData().text}
        shareType="devotional"
      />
    </div>
  );
};

export default DailyDevotionalPage;
